import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/shared/ui/primitives'
import { Icon } from '@/app/icons'

/** The pieces every landing card shares. */

/** The same surface as <Panel> in the page kit, as a class for elements that are links or buttons. */
export const CARD = 'min-w-0 rounded-panel border border-line bg-card shadow-panel'

export function CardHead({
  title,
  count,
  countTone = 'primary',
  sub,
  link,
}: {
  title: string
  count?: number
  countTone?: 'primary' | 'danger'
  sub?: string
  link?: { to: string; label: string }
}) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-4">
      <h2 className="text-base font-bold tracking-tight text-gray-900">{title}</h2>
      {count !== undefined && count > 0 && (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-bold tabular-nums',
            countTone === 'danger' ? 'bg-danger-light text-danger' : 'bg-primary-light text-primary',
          )}
        >
          {count}
        </span>
      )}
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
      {link && (
        <Link
          to={link.to}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-semibold whitespace-nowrap text-primary hover:bg-primary-light"
        >
          {link.label}
          <Icon name="arrowRight" className="size-4" />
        </Link>
      )}
    </div>
  )
}

/** A quiet "nothing here" row, with a tick when empty is good news. */
export function EmptyRow({ title, text, good = true, icon }: { title: string; text: string; good?: boolean; icon?: ReactNode }) {
  return (
    <div className="flex items-center gap-4 px-5 py-7">
      <span
        className={cn(
          'grid size-12 shrink-0 place-items-center rounded-xl',
          good ? 'bg-success-light text-success' : 'bg-gray-200 text-gray-500',
        )}
      >
        {icon ?? <Icon name="check" className="size-5" />}
      </span>
      <div>
        <p className="font-semibold text-gray-900">{title}</p>
        <p className="mt-0.5 text-gray-500">{text}</p>
      </div>
    </div>
  )
}

// One badge and one ladder for the whole app: the module screens use the same ones.
export { StatusPill } from '@/features/billing/proforma/StatusPill'
export { LevelBars } from '@/features/billing/approval/LevelBars'

export { useJakartaNow } from '@/shared/timeOfDay'
